import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useCart } from '../../../contexts/CartContext';
import { useCategories } from '../../../hooks/useCategories';
import { useThemeContext } from '../../../contexts/ThemeContext';
import { useAuth } from '../../../contexts/AuthContext';

interface NavbarProps {
  onSearchOpen: () => void;
  onLoginOpen: () => void;
}

export default function Navbar({ onSearchOpen, onLoginOpen }: NavbarProps) {
  const { totalItems, openCart } = useCart();
  const { categories, loading: categoriesLoading } = useCategories();
  const { theme } = useThemeContext();
  const { user, signOut } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const categoriesRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const isHome = location.pathname === '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (categoriesRef.current && !categoriesRef.current.contains(e.target as Node)) {
        setCategoriesOpen(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const navBg = scrolled || !isHome ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-transparent';
  const textColor = scrolled || !isHome ? 'text-coffee' : 'text-white';
  const iconColor = scrolled || !isHome ? 'text-coffee' : 'text-white';

  // All nav links in order (auto items rendered as dropdown, others as plain links)
  const allLinks = theme.navbar.links;

  const renderDesktopLink = (link: { label: string; href: string; isAuto?: boolean }, idx: number) => {
    if (link.isAuto) {
      return (
        <div key={`auto-${idx}`} className="relative" ref={categoriesRef}>
          <button
            className={`flex items-center gap-1 text-sm font-medium transition-colors hover:text-gold ${textColor} cursor-pointer whitespace-nowrap`}
            onClick={() => setCategoriesOpen(!categoriesOpen)}
          >
            {link.label}
            <i className={`ri-arrow-down-s-line text-base transition-transform ${categoriesOpen ? 'rotate-180' : ''}`} />
          </button>
          {categoriesOpen && (
            <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-64 bg-white rounded-2xl shadow-lg border border-cream-dark overflow-hidden">
              <div className="p-2">
                <Link to="/urunler" className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-cream transition-colors text-coffee" onClick={() => setCategoriesOpen(false)}>
                  <div className="w-8 h-8 flex items-center justify-center bg-gold/10 rounded-lg">
                    <i className="ri-store-2-line text-gold text-sm" />
                  </div>
                  <span className="font-medium text-sm">Tüm Ürünler</span>
                </Link>
                {categoriesLoading ? (
                  <div className="px-4 py-3 text-xs text-coffee-light">Yükleniyor...</div>
                ) : (
                  categories.map(cat => (
                    <Link
                      key={cat.id}
                      to={`/urunler?kategori=${encodeURIComponent(cat.name)}`}
                      className="flex items-center justify-between px-4 py-3 rounded-xl hover:bg-cream transition-colors text-coffee"
                      onClick={() => setCategoriesOpen(false)}
                    >
                      <span className="font-medium text-sm">{cat.name}</span>
                      {cat.product_count > 0 && (
                        <span className="text-xs text-coffee-light bg-cream px-2 py-0.5 rounded-full">{cat.product_count}</span>
                      )}
                    </Link>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      );
    }
    return (
      <Link key={`${link.href}-${idx}`} to={link.href} className={`text-sm font-medium transition-colors hover:text-gold whitespace-nowrap ${textColor}`}>
        {link.label}
      </Link>
    );
  };

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navBg}`}>
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* Logo */}
            <Link to="/" className="flex-shrink-0">
              <img
                src={theme.general.logo_url}
                alt={theme.general.site_name}
                className="h-10 lg:h-12 w-auto object-contain"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-8">
              {allLinks.map((link, idx) => renderDesktopLink(link, idx))}
            </nav>

            {/* Right Actions */}
            <div className="flex items-center gap-1 lg:gap-2">
              <button onClick={onSearchOpen} className={`w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors ${iconColor} cursor-pointer`} aria-label="Ara">
                <i className="ri-search-line text-lg" />
              </button>
              {user ? (
                <div className="relative hidden lg:block" ref={userMenuRef}>
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-full hover:bg-white/10 transition-colors ${iconColor} cursor-pointer`}
                    aria-label="Hesabım"
                  >
                    <div className="w-7 h-7 flex items-center justify-center bg-gold rounded-full text-white text-xs font-bold flex-shrink-0">
                      {(user.user_metadata?.full_name || user.email || 'U')[0].toUpperCase()}
                    </div>
                    <span className="text-sm font-medium max-w-[100px] truncate">
                      {user.user_metadata?.full_name?.split(' ')[0] || user.email?.split('@')[0]}
                    </span>
                    <i className={`ri-arrow-down-s-line text-sm transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {userMenuOpen && (
                    <div className="absolute top-full right-0 mt-2 w-52 bg-white rounded-2xl border border-cream-dark overflow-hidden z-50">
                      <div className="px-4 py-3 border-b border-cream-dark">
                        <p className="text-xs font-semibold text-coffee truncate">{user.user_metadata?.full_name || 'Kullanıcı'}</p>
                        <p className="text-xs text-coffee-light truncate">{user.email}</p>
                      </div>
                      <div className="p-2">
                        <Link
                          to="/profil"
                          className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-cream transition-colors text-coffee text-sm"
                          onClick={() => setUserMenuOpen(false)}
                        >
                          <i className="ri-user-line text-base w-4 h-4 flex items-center justify-center" />
                          Profilim
                        </Link>
                        <button
                          onClick={() => { signOut(); setUserMenuOpen(false); }}
                          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-red-50 transition-colors text-red-600 text-sm cursor-pointer"
                        >
                          <i className="ri-logout-box-r-line text-base w-4 h-4 flex items-center justify-center" />
                          Çıkış Yap
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <button onClick={onLoginOpen} className={`hidden lg:flex w-10 h-10 items-center justify-center rounded-full hover:bg-white/10 transition-colors ${iconColor} cursor-pointer`} aria-label="Hesabım">
                  <i className="ri-user-line text-lg" />
                </button>
              )}
              <button onClick={() => navigate('/profil')} className={`hidden lg:flex w-10 h-10 items-center justify-center rounded-full hover:bg-white/10 transition-colors ${iconColor} cursor-pointer`} aria-label="Favoriler">
                <i className="ri-heart-line text-lg" />
              </button>
              <button onClick={openCart} className={`relative w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors ${iconColor} cursor-pointer`} aria-label="Sepet">
                <i className="ri-shopping-bag-line text-lg" />
                {totalItems > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-5 h-5 flex items-center justify-center bg-gold text-white text-xs font-bold rounded-full">
                    {totalItems > 9 ? '9+' : totalItems}
                  </span>
                )}
              </button>
              <button onClick={() => setMenuOpen(!menuOpen)} className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors ${iconColor} cursor-pointer`}>
                <i className={`text-xl ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`} />
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {menuOpen && (
          <div className="lg:hidden bg-white border-t border-cream-dark">
            <div className="max-w-7xl mx-auto px-4 py-4 space-y-1">
              {allLinks.map((link, idx) =>
                link.isAuto ? (
                  <div key={`mob-auto-${idx}`}>
                    <Link to="/urunler" className="block px-4 py-3 text-sm font-medium text-coffee hover:bg-cream rounded-xl transition-colors" onClick={() => setMenuOpen(false)}>
                      {link.label}
                    </Link>
                    {categories.map(cat => (
                      <Link key={cat.id} to={`/urunler?kategori=${encodeURIComponent(cat.name)}`} className="block px-8 py-2 text-sm text-coffee-light hover:bg-cream rounded-xl transition-colors" onClick={() => setMenuOpen(false)}>
                        {cat.name}
                      </Link>
                    ))}
                  </div>
                ) : (
                  <Link key={`mob-${link.href}-${idx}`} to={link.href} className="block px-4 py-3 text-sm font-medium text-coffee hover:bg-cream rounded-xl transition-colors" onClick={() => setMenuOpen(false)}>
                    {link.label}
                  </Link>
                )
              )}
              <div className="pt-2 px-4">
                {user ? (
                  <div className="space-y-1">
                    <Link to="/profil" className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-cream transition-colors text-coffee text-sm" onClick={() => setMenuOpen(false)}>
                      <i className="ri-user-line" /> Profilim
                    </Link>
                    <button
                      onClick={() => { signOut(); setMenuOpen(false); }}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-red-50 transition-colors text-red-600 text-sm cursor-pointer"
                    >
                      <i className="ri-logout-box-r-line" /> Çıkış Yap
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-3">
                    <button onClick={() => { onLoginOpen(); setMenuOpen(false); }} className="flex-1 py-2.5 text-sm font-medium text-coffee border border-coffee-light rounded-xl hover:bg-coffee hover:text-white transition-colors cursor-pointer whitespace-nowrap">Giriş Yap</button>
                    <button onClick={() => { onLoginOpen(); setMenuOpen(false); }} className="flex-1 py-2.5 text-sm font-medium bg-gold text-white rounded-xl hover:bg-gold-dark transition-colors cursor-pointer whitespace-nowrap">Üye Ol</button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </header>
    </>
  );
}
